import React from "react";
import { extractSectionCode, cleanSectionName } from "./sectionCode";

/**
 * 14030 — Left rail of the Questionnaire & Risk Analysis tab.
 *
 * Each row shows:
 *   [code chip]  Section name…       10/10   10/10
 *                Green 100.0%        ●10
 *
 * Where the two 10/10 counts are "scored / total" and "compliant / total"
 * (mirrors the section header band). RAG band + score come from per-section
 * aggregates computed upstream and passed in via `aggregates`. When risk is
 * not entitled or no scored rows exist, the row falls back to the plain
 * approved/total badge so the sidebar still works for non-risk viewers.
 */
const RAG_CLASS = {
  Green: "bg-green-100 text-green-800 border-green-200",
  Yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
  Red: "bg-red-100 text-red-800 border-red-200",
};

const SectionCodeChip = ({ code, active }) => (
  <span
    className={`inline-flex items-center justify-center min-w-[2.25rem] h-7 px-1.5 rounded text-xs font-semibold ${
      active
        ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
        : "bg-gray-100 text-gray-700 border border-gray-200"
    }`}
  >
    {code}
  </span>
);

const VqqSectionSidebar = ({
  sections,
  activeSection,
  onSelect,
  aggregatesBySection = null,
}) => {
  if (!sections || sections.length === 0) return null;

  return (
    <nav aria-label="Questionnaire sections" className="w-72 shrink-0">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2 px-1">
        Sections
      </div>
      <ul className="space-y-1">
        {sections.map((section) => {
          const isActive = section.section === activeSection;
          const total = section.question_count ?? 0;
          const approved = section.approved_count ?? 0;
          const code = extractSectionCode(section.section);
          const label = cleanSectionName(section.section);
          const agg = aggregatesBySection?.get(section.section);
          const rag = agg?.rag;
          const scoreText =
            agg?.score != null
              ? `${rag} ${agg.score.toFixed(1)}%`
              : null;

          return (
            <li key={section.section}>
              <button
                type="button"
                onClick={() => onSelect(section.section)}
                className={`w-full text-left rounded-md px-2 py-2 text-sm transition ${
                  isActive
                    ? "bg-indigo-50 border border-indigo-200"
                    : "border border-transparent hover:bg-gray-50"
                }`}
                aria-current={isActive ? "true" : undefined}
              >
                <div className="flex items-center gap-2.5">
                  <SectionCodeChip code={code} active={isActive} />
                  <span
                    className={`flex-1 truncate ${
                      isActive ? "text-indigo-700 font-medium" : "text-gray-700"
                    }`}
                  >
                    {label}
                  </span>
                  <span className="shrink-0 text-[10px] font-medium text-gray-500 tabular-nums">
                    {agg ? `${agg.scored}/${agg.total}` : `${approved}/${total}`}
                  </span>
                  <span className="shrink-0 text-[10px] font-medium text-gray-500 tabular-nums">
                    {agg ? `${agg.compliant}/${agg.total}` : ""}
                  </span>
                </div>
                {scoreText ? (
                  <div className="mt-1 pl-[2.85rem] flex items-center justify-between gap-2">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                        RAG_CLASS[rag] || RAG_CLASS.Green
                      }`}
                    >
                      {scoreText}
                    </span>
                    <FindingDots buckets={agg?.findingBuckets} />
                  </div>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

const FindingDots = ({ buckets }) => {
  if (!buckets) return null;
  const items = [
    { key: "critical", count: buckets.critical, dot: "bg-red-500" },
    { key: "high",     count: buckets.high,     dot: "bg-orange-500" },
    { key: "medium",   count: buckets.medium,   dot: "bg-yellow-500" },
    { key: "low",      count: buckets.low,      dot: "bg-blue-500" },
    { key: "compliant",count: buckets.compliant,dot: "bg-emerald-500" },
  ].filter((x) => x.count > 0);
  if (items.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-2">
      {items.map((it) => (
        <span key={it.key} className="inline-flex items-center gap-0.5 text-[10px] text-gray-600">
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${it.dot}`} aria-hidden="true" />
          <span className="tabular-nums">{it.count}</span>
        </span>
      ))}
    </span>
  );
};

export default VqqSectionSidebar;
